import { ApplicationUser, DeleteResourceUserRequest } from "@api/apiClient";
import { useDeleteResourceUser, useGetResourceUsers } from "@api/resourceUsers";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import { Box, Snackbar, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridRowId } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";

interface ProjectUsersGridProps {
  projectId: string;
}

const ProjectUsersGrid = observer((props: ProjectUsersGridProps) => {
  const [openDeleteToast, setOpenDeleteToast] = useState(false);
  const [rows, setRows] = useState<ApplicationUser[]>([]);
  // // Api calls
  const resourceUsers = useGetResourceUsers(props.projectId, false);
  const deleteResourceUser = useDeleteResourceUser();
  const confirm = useConfirm();

  useEffect(() => {
    setRows([]);
    const newRows = resourceUsers.data || [];
    setRows(newRows);
  }, [resourceUsers.data]);

  const handleDeleteClick = async (id: GridRowId) => {
    const { confirmed } = await confirm({
      title: i18n.t("dashboard-add-user.confirm-dialog.header"),
      description: i18n.t("dashboard-add-user.confirm-dialog.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      const deletingRow = rows.find((row) => row.id === id);
      if (deletingRow?.id) {
        await deleteResourceUser.mutateAsync(new DeleteResourceUserRequest({ resourceId: props.projectId, userId: deletingRow.id }) as any);
        setOpenDeleteToast(true);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: "email",
      headerName: i18n.t("dashboard-add-user.user-tab.datagrid-header-column-email"),
      editable: false,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("dashboard-add-user.user-tab.datagrid-header-column-action"),
      resizable: false,
      width: 80,
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            key={`delete-${id}`}
            icon={
              <Tooltip title={i18n.t("dashboard-add-user.user-tab.datagrid-delete-button-tooltip")}>
                <GridDeleteIcon />
              </Tooltip>
            }
            label={i18n.t("dashboard-add-user.user-tab.datagrid-delete-button-tooltip")}
            onClick={() => handleDeleteClick(id)}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Box sx={{ width: "100%", height: "100%" }}>
        <DataGrid
          loading={resourceUsers.isFetching || resourceUsers.isPending}
          columns={columns}
          rows={rows}
          disableColumnMenu
          density="compact"
          initialState={{
            sorting: {
              sortModel: [{ field: "email", sort: "asc" }],
            },
          }}
        />
      </Box>
      <Snackbar
        open={openDeleteToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("dashboard-add-user.user-tab.delete-success-message")}
        onClose={() => setOpenDeleteToast(false)}
      />
    </>
  );
});

export default ProjectUsersGrid;
